import {
  createBrowserHostedSessionClient,
  createLocalStorageOwlbertSessionStore,
  isOwlbertProFromLicenseFeatureIds,
  normalizeHostedBackendBaseUrl,
  type BrowserHostedSessionClient,
} from '@owlbert/core'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { resolveHostedBackendUrl } from '../config/hostedBackendUrl'
import { webLog } from '../logging/webLog'

const WORKSPACE_STORAGE_KEY = 'owlbert_web_workspace_id'

const DEFAULT_HOSTED_WORKSPACE_NAME = 'Default'

/**
 * Ensures the signed-in user has at least one cloud workspace: creates "Default" when the list is empty.
 * Handles create races (e.g. duplicate name) by re-listing after a failed create.
 */
async function listHostedWorkspacesEnsuringDefault(
  bundle: BrowserHostedSessionClient,
): Promise<OwlbertWebWorkspace[]> {
  let ws = await bundle.client.owlbertHosted.workspaces.list.query()
  if (ws.length > 0) return ws
  try {
    webLog('info', 'auth', 'hosted workspaces empty; creating default', { name: DEFAULT_HOSTED_WORKSPACE_NAME })
    const created = await bundle.client.owlbertHosted.workspaces.create.mutate({
      name: DEFAULT_HOSTED_WORKSPACE_NAME,
    })
    return [{ id: created.id, name: created.name }]
  } catch (e) {
    ws = await bundle.client.owlbertHosted.workspaces.list.query()
    if (ws.length > 0) return ws
    throw e
  }
}

export type OwlbertWebEntitlement = {
  features: string[]
  featureDetails: Array<{ license_id: string; friendly_name: string }>
}

export type OwlbertWebUser = {
  id: string
  email: string | null
  isAdmin: boolean
}

export type OwlbertWebWorkspace = { id: string; name: string }

/**
 * Outcome of `signup`. Supabase returns a session immediately only when email confirmation is
 * disabled; with confirmation on, the account exists but the user cannot be signed in yet, and the
 * UI must say so rather than silently appearing to do nothing.
 */
export type OwlbertWebSignupResult = { status: 'signed_in' } | { status: 'confirmation_required' }

export type OwlbertWebAuthContextValue = {
  backendUrl: string
  configured: boolean
  sessionReady: boolean
  user: OwlbertWebUser | null
  workspaceId: string | null
  workspaces: OwlbertWebWorkspace[]
  setWorkspaceId: (id: string) => void
  loading: boolean
  error: string | null
  bundle: BrowserHostedSessionClient | null
  /** Desktop-style license features from `entitlement.get` (null if unavailable or signed out). */
  entitlement: OwlbertWebEntitlement | null
  /** True when the account includes the `owlbert:pro` feature (see desktop `useIsProMode`). */
  isProMode: boolean
  login: (email: string, password: string) => Promise<void>
  /** Create an account. Signs in immediately unless the backend requires email confirmation. */
  signup: (email: string, password: string) => Promise<OwlbertWebSignupResult>
  /** False when the backend has signup disabled (SIGNUP_OPEN=false); null until known. */
  signupAvailable: boolean | null
  logout: () => Promise<void>
  refreshWorkspaces: () => Promise<void>
}

const OwlbertWebAuthContext = createContext<OwlbertWebAuthContextValue | null>(null)

let sharedSessionStore: ReturnType<typeof createLocalStorageOwlbertSessionStore> | null = null

function getSharedSessionStore() {
  if (!sharedSessionStore) sharedSessionStore = createLocalStorageOwlbertSessionStore()
  return sharedSessionStore
}

export const OwlbertWebAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { url: backendUrl } = resolveHostedBackendUrl()
  const configured = Boolean(backendUrl.trim())

  const sessionStore = useMemo(() => getSharedSessionStore(), [])

  const bundle = useMemo(() => {
    if (!configured) return null
    return createBrowserHostedSessionClient({ baseUrl: backendUrl, sessionStore })
  }, [configured, backendUrl, sessionStore])

  const [user, setUser] = useState<OwlbertWebUser | null>(null)
  const [workspaceId, setWorkspaceIdState] = useState<string | null>(null)
  const [workspaces, setWorkspaces] = useState<OwlbertWebWorkspace[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [entitlement, setEntitlement] = useState<OwlbertWebEntitlement | null>(null)
  const [signupAvailable, setSignupAvailable] = useState<boolean | null>(null)

  const isProMode = useMemo(() => isOwlbertProFromLicenseFeatureIds(entitlement?.features), [entitlement])

  const hydrate = useCallback(async () => {
    if (!bundle) {
      setUser(null)
      setWorkspaces([])
      setWorkspaceIdState(null)
      setEntitlement(null)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      webLog('info', 'auth', 'hydrate: checking session store')
      const session = sessionStore.getSession()
      if (!session?.access_token && !session?.refresh_token) {
        webLog('info', 'auth', 'hydrate: no tokens, cleared user state')
        setUser(null)
        setWorkspaces([])
        setWorkspaceIdState(null)
        setEntitlement(null)
        return
      }
      let me: { id: string; email: string | null; isAdmin: boolean }
      try {
        const row = await bundle.client.auth.me.query()
        me = { id: row.id, email: row.email ?? null, isAdmin: row.isAdmin }
      } catch (e) {
        webLog('error', 'auth', 'hydrate: auth.me failed', { message: String((e as Error)?.message ?? e) })
        setUser(null)
        setWorkspaces([])
        setWorkspaceIdState(null)
        setEntitlement(null)
        setError(String((e as Error)?.message ?? e))
        sessionStore.clearSession()
        if (typeof window !== 'undefined') window.localStorage.removeItem(WORKSPACE_STORAGE_KEY)
        return
      }
      setUser(me)
      webLog('info', 'auth', 'hydrate: signed in', { userId: me.id, email: me.email })

      setEntitlement(null)
      try {
        const e = await bundle.client.entitlement.get.query()
        setEntitlement({
          features: e.features,
          featureDetails: (e.featureDetails ?? []).map((fd) => ({
            license_id: fd.license_id,
            friendly_name: fd.friendly_name,
          })),
        })
      } catch (entErr) {
        webLog('warn', 'auth', 'entitlement.get failed', { message: String((entErr as Error)?.message ?? entErr) })
      }

      try {
        const ws = await listHostedWorkspacesEnsuringDefault(bundle)
        webLog('info', 'auth', 'hydrate: workspaces loaded', { count: ws.length, ids: ws.map((w) => w.id) })
        setWorkspaces(ws)
        const stored = typeof window !== 'undefined' ? window.localStorage.getItem(WORKSPACE_STORAGE_KEY) : null
        const pick =
          (stored && ws.some((w) => w.id === stored) ? stored : null) ||
          ws[0]?.id ||
          null
        setWorkspaceIdState(pick)
        if (pick && typeof window !== 'undefined') window.localStorage.setItem(WORKSPACE_STORAGE_KEY, pick)
        setError(null)
      } catch (e) {
        webLog('error', 'auth', 'hydrate: workspaces failed', { message: String((e as Error)?.message ?? e) })
        setWorkspaces([])
        setWorkspaceIdState(null)
        if (typeof window !== 'undefined') window.localStorage.removeItem(WORKSPACE_STORAGE_KEY)
        setError(`Signed in, but workspaces could not be loaded: ${String((e as Error)?.message ?? e)}`)
      }
    } catch (e) {
      webLog('error', 'auth', 'hydrate: unexpected failure', { message: String((e as Error)?.message ?? e) })
      setUser(null)
      setWorkspaces([])
      setWorkspaceIdState(null)
      setEntitlement(null)
      setError(String((e as Error)?.message ?? e))
      sessionStore.clearSession()
      if (typeof window !== 'undefined') window.localStorage.removeItem(WORKSPACE_STORAGE_KEY)
    } finally {
      setLoading(false)
    }
  }, [bundle, sessionStore])

  useEffect(() => {
    void hydrate()
  }, [hydrate])

  const setWorkspaceId = useCallback((id: string) => {
    setWorkspaceIdState(id)
    if (typeof window !== 'undefined') window.localStorage.setItem(WORKSPACE_STORAGE_KEY, id)
  }, [])

  // Whether the backend accepts new signups; drives the "Create account" affordance.
  useEffect(() => {
    if (!bundle) {
      setSignupAvailable(null)
      return
    }
    let cancelled = false
    bundle.client.auth.signupAvailable
      .query()
      .then((r: { signupAvailable: boolean; inviteOnly: boolean }) => {
        if (!cancelled) setSignupAvailable(r.signupAvailable)
      })
      .catch((e: unknown) => {
        webLog('warn', 'auth', 'signupAvailable query failed', { message: String((e as Error)?.message ?? e) })
        if (!cancelled) setSignupAvailable(null)
      })
    return () => {
      cancelled = true
    }
  }, [bundle])

  const login = useCallback(
    async (email: string, password: string) => {
      if (!bundle) throw new Error('Hosted session client is not available')
      setError(null)
      await bundle.auth.signIn(email, password)
      await hydrate()
    },
    [bundle, hydrate],
  )

  const signup = useCallback(
    async (email: string, password: string): Promise<OwlbertWebSignupResult> => {
      if (!bundle) throw new Error('Hosted session client is not available')
      setError(null)
      // `auth.signUp` persists the session to the shared store when one is returned.
      const result = await bundle.auth.signUp(email, password)
      if (!result.session?.access_token) {
        webLog('info', 'auth', 'signup: awaiting email confirmation')
        return { status: 'confirmation_required' }
      }
      await hydrate()
      return { status: 'signed_in' }
    },
    [bundle, hydrate],
  )

  const logout = useCallback(async () => {
    if (!bundle) return
    try {
      await bundle.auth.signOut()
    } finally {
      setUser(null)
      setWorkspaces([])
      setWorkspaceIdState(null)
      setEntitlement(null)
      if (typeof window !== 'undefined') window.localStorage.removeItem(WORKSPACE_STORAGE_KEY)
    }
  }, [bundle])

  const refreshWorkspaces = useCallback(async () => {
    if (!bundle || !user) return
    const ws = await listHostedWorkspacesEnsuringDefault(bundle)
    setWorkspaces(ws)
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(WORKSPACE_STORAGE_KEY) : null
    const pick =
      (stored && ws.some((w) => w.id === stored) ? stored : null) || ws[0]?.id || null
    if (pick) setWorkspaceIdState(pick)
    if (pick && typeof window !== 'undefined') window.localStorage.setItem(WORKSPACE_STORAGE_KEY, pick)
  }, [bundle, user])

  const displayBackendUrl = normalizeHostedBackendBaseUrl(backendUrl)

  const value: OwlbertWebAuthContextValue = {
    backendUrl: displayBackendUrl,
    configured,
    sessionReady: Boolean(user),
    user,
    workspaceId,
    workspaces,
    setWorkspaceId,
    loading,
    error,
    bundle,
    entitlement,
    isProMode,
    login,
    signup,
    signupAvailable,
    logout,
    refreshWorkspaces,
  }

  return <OwlbertWebAuthContext.Provider value={value}>{children}</OwlbertWebAuthContext.Provider>
}

export function useOwlbertWebAuth(): OwlbertWebAuthContextValue {
  const v = useContext(OwlbertWebAuthContext)
  if (!v) throw new Error('useOwlbertWebAuth must be used within OwlbertWebAuthProvider')
  return v
}
